import clsx from "clsx";
import { useState } from "react";
import { useUpdateProjectStatusMutation } from "../../../features/projectApi";
import { addBannerWithTimeout } from "../../lib/centralizedBanner/slice";
import { useAppDispatch } from "../../lib/redux/hooks";
import { colors } from "../../ui/colors";
import { PlusIcon } from "../../ui/icons/plus";
import Button from "../../ui/molecules/button";
import Pills from "../../ui/molecules/pills";
import { Spinner } from "../../ui/molecules/spinner";
import { FindAllProjectsResponse, ProjectStateEnum } from "./dto";

interface Props {
  show: boolean;
  setShow: (show: boolean) => void;
  response: FindAllProjectsResponse | undefined;
}

export function ProjectChangeStatusModal({ show, setShow, response }: Props) {
  const [changeStatus, { isLoading }] = useUpdateProjectStatusMutation();

  const handleChangeStatus = async () => {
    try {
      await changeStatus({
        request: request
      }).unwrap();
      dispatch(addBannerWithTimeout({
        variant: "success",
        message: "Stato progetti aggiornato con successo."
      }))
      setShow(false);
    } catch (error) {
      setShow(false);
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Si è verificato un errore durante l'aggiornamento dello stato dei progetti."
      }))
    }
  };

  const [request, setRequest] = useState<
    {
      id: string;
      state:
      | undefined
      | ProjectStateEnum.CONCLUDED
      | ProjectStateEnum.INPROGRESS;
    }[]
  >(
    response?.data.map((project) => ({
      id: project.id,
      state: ProjectStateEnum.INPROGRESS,
    }))!
  );
  const dispatch = useAppDispatch();

  return (
    <div
      onClick={() => setShow(false)}
      className={clsx(
        "transition-all fixed bg-opacity-30 bg-neutral-950 backdrop-blur-sm top-0 left-0 h-full w-full flex items-center justify-center z-[100]",
        {
          block: show,
          hidden: !show,
        }
      )}
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <div
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          className="transition-all relative h-2/3 flex flex-col items-center bg-white rounded-[12px] opacity-100 shadow-sm min-w-[400px]"
        >
          <div
            onClick={() => setShow(false)}
            className="absolute cursor-pointer rotate-45 top-[-16px] right-[-16px] border border-brandPrimary-600 h-8 w-8 rounded-full flex items-center justify-center bg-brandPrimary-50"
          >
            <PlusIcon color={colors.brandPrimary[600]} size={16} />
          </div>
          <div className="p-6 text-heading-xs font-semibold text-center">
            {"Progetti sospesi"}
          </div>
          <div className="px-6 py-8 h-full flex flex-col justify-center items-center gap-6 border-t-neutral-200 border-t">
            <span className="text-text-md text-center font-normal text-neutral-600">
              Abbiamo notato che ci sono progetti inattivi da 5 giorni.
              <br /> Seleziona "Concluso" per terminarne le attività.
            </span>
            <div className="flex flex-col w-full h-full gap-3 overflow-auto pr-3">
              {response?.data.map((project, index) => (
                <div
                  key={project.id}
                  className={clsx("flex items-center justify-between", {
                    "border-b border-b-neutral-300 pb-3 border-dashed":
                      index !== response?.total! - 1,
                  })}
                >
                  <span className="text-text-md font-semibold text-neutral-600">
                    {project.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        const newRequest = [...request];
                        const index = newRequest.findIndex(
                          (value) => value.id === project.id
                        );
                        newRequest[index] = {
                          ...newRequest[index],
                          state: ProjectStateEnum.INPROGRESS,
                        };
                        setRequest([...newRequest]);
                      }}
                    >
                      <Pills
                        size={"sm"}
                        label={"In corso"}
                        outline={false}
                        emphasis={
                          request.find((_project) => project.id === _project.id)
                            ?.state === ProjectStateEnum.INPROGRESS
                        }
                        color={"blue"}
                      />
                    </div>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        const newRequest = [...request];
                        const index = newRequest.findIndex(
                          (value) => value.id === project.id
                        );
                        newRequest[index] = {
                          ...newRequest[index],
                          state: ProjectStateEnum.CONCLUDED,
                        };
                        setRequest([...newRequest]);
                      }}
                    >
                      <Pills
                        size={"sm"}
                        label={"Concluso"}
                        outline={false}
                        emphasis={
                          request.find((_project) => project.id === _project.id)
                            ?.state === ProjectStateEnum.CONCLUDED
                        }
                        color={"green"}
                      />
                    </div>
                  </div>
                </div>
              )
              )}
            </div>
            <div className="w-full flex flex-1 gap-3 mt-3">
              <div className="w-1/2">
                <Button
                  fullWidth
                  size={"sm"}
                  iconPosition={"off"}
                  variant={"outline"}
                  color={"gray"}
                  label="Annulla"
                  onClick={() => setShow(false)}
                />
              </div>
              <div className="w-1/2">
                <Button
                  fullWidth
                  size={"sm"}
                  iconPosition={"off"}
                  variant={"solid"}
                  color={"blue"}
                  label="Conferma"
                  onClick={() => handleChangeStatus()}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
